<template>
  <div v-if="!multipleItemsSelected">
    <div
      v-for="(info, index) in information"
      :key="index"
      class="w-full h-[30px] font-primary border-b border-[#E5E5E5] flex items-center justify-between text-[12px] leading-[16px] text-gray-800"
    >
      <div class="opacity-60">{{ info.label }}</div>
      <component :is="info.value" />
    </div>
    <div
      v-if="isFileItem"
      class="font-secondary text-[12px] text-green-700 text-right font-medium mt-1 cursor-pointer"
      @click="handleFileSettingsClick"
    >
      Edit copyright settings
    </div>
  </div>
</template>

<script setup lang="ts">
import { CopyrightStatus, type File } from '@/shared/graphql/router/graphql'
import type { SidebarItem } from './Sidebar.vue'
import { formatTimestamp } from '@/shared/utils/datetime'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { computed, h } from 'vue'

const props = defineProps<{
  item: SidebarItem
  isFileItem: boolean
  multipleItemsSelected: boolean
}>()

const { open } = useDialog()

const folderInformation = computed(() => {
  const totalFiles = 'totalFiles' in props.item ? props.item.totalFiles : 0
  const createdAt = 'createdAt' in props.item ? props.item.createdAt : undefined

  return [
    {
      label: 'Number of files',
      value: h('div', { class: 'font-secondary' }, [totalFiles]),
    },
    ...(createdAt
      ? [
          {
            label: 'Created',
            value: h('div', { class: 'font-secondary' }, [
              formatTimestamp(createdAt),
            ]),
          },
        ]
      : []),
  ]
})

const fileInformation = computed(() => {
  if (!props.isFileItem) return []
  const file = props.item as File

  return [
    {
      label: 'Size',
      value: h('div', { class: 'font-secondary' }, file.metadata?.size || 0),
    },
    {
      label: 'Type',
      value: h('div', { class: 'font-secondary' }, file.type),
    },
    {
      label: 'Visibility',
      value: h(
        'div',
        { class: 'flex items-center font-secondary' },
        file.copyright?.status === CopyrightStatus.Inactive
          ? '- -'
          : file.settings?.isPrivate
            ? 'Private'
            : 'Public'
      ),
    },
  ].filter((item) => !!item)
})

const information = computed(() =>
  props.isFileItem ? fileInformation.value : folderInformation.value
)

const handleFileSettingsClick = () => {
  if (!props.isFileItem) return

  open(Dialogs.FileSettings, {
    props: { file: props.item as File },
  })
}
</script>
